import { useCallback, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MuiMenuItem from "@mui/material/MenuItem";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useUpdateOpportunity, useDeleteOpportunity } from "@/hooks/queries";
import { useNotification } from "@/hooks/useNotification";
import type { Opportunity, OpportunitySubcategory } from "@api/opportunity";
import type { ColumnDef } from "./columnConfig";
import { getDisplayValue, isCellReadOnly } from "./opportunityCells";
import styles from "./styles.module.css";

type Props = {
  opportunity: Opportunity;
  isAdmin: boolean;
  columns: ColumnDef[];
  categoryName: string;
  subcategories: OpportunitySubcategory[];
};

type EditValues = {
  primary_date: string | null;
  subcategory_id: string | null;
  fields: Record<string, string | null>;
};

const buildEditValues = (opp: Opportunity): EditValues => ({
  primary_date: opp.primary_date,
  subcategory_id: opp.subcategory_id,
  fields: { ...opp.fields },
});

const getEditValue = (editValues: EditValues, col: ColumnDef): string | null => {
  if (col.isPrimarySort) return editValues.primary_date;
  return editValues.fields[col.field] ?? null;
};

export const OpportunityCard = ({ opportunity, isAdmin, columns, categoryName, subcategories }: Props) => {
  const updateMutation = useUpdateOpportunity();
  const deleteMutation = useDeleteOpportunity();
  const { notifySuccess, notifyError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>(buildEditValues(opportunity));
  const hasSubcategories = subcategories.length > 0;
  // Non-admins can edit only when the category has an Open-Edit column; isCellReadOnly() further limits
  // them to those cells. Delete and subcategory changes stay admin-only.
  const canEdit = isAdmin || columns.some((c) => c.openEdit);

  const handleEdit = useCallback(() => {
    setEditValues(buildEditValues(opportunity));
    setIsEditing(true);
  }, [opportunity]);

  const handleCancel = useCallback(() => setIsEditing(false), []);

  const handleSave = useCallback(() => {
    updateMutation.mutate(
      {
        id: opportunity.id,
        payload: {
          primary_date: editValues.primary_date,
          subcategory_id: editValues.subcategory_id,
          fields: editValues.fields,
        },
      },
      {
        onSuccess: () => { notifySuccess("Opportunity updated"); setIsEditing(false); },
        onError: () => notifyError("Failed to update opportunity"),
      }
    );
  }, [opportunity.id, editValues, updateMutation, notifySuccess, notifyError]);

  const handleDelete = useCallback(() => {
    if (window.confirm("Delete this opportunity?")) {
      deleteMutation.mutate(opportunity.id, {
        onSuccess: () => notifySuccess("Opportunity deleted"),
        onError: () => notifyError("Failed to delete opportunity"),
      });
    }
  }, [opportunity.id, deleteMutation, notifySuccess, notifyError]);

  const handleFieldChange = useCallback(
    (field: string, isPrimarySort: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isPrimarySort) {
        setEditValues((prev) => ({ ...prev, primary_date: e.target.value }));
      } else {
        setEditValues((prev) => ({
          ...prev,
          fields: { ...prev.fields, [field]: e.target.value },
        }));
      }
    },
    []
  );

  const handleDateChange = useCallback(
    (field: string, isPrimarySort: boolean) => (newVal: Dayjs | null) => {
      const value = newVal ? newVal.toISOString() : null;
      if (isPrimarySort) {
        setEditValues((prev) => ({ ...prev, primary_date: value }));
      } else {
        setEditValues((prev) => ({
          ...prev,
          fields: { ...prev.fields, [field]: value },
        }));
      }
    },
    []
  );

  const dateCol = columns.find((c) => c.isPrimarySort) ?? columns[0];
  const titleCol =
    columns.find((c) => c.field === "artist_tour") ??
    columns.find((c) => !c.isPrimarySort && !c.isInt) ??
    columns[0];
  const detailCols = columns.filter((c) => c !== dateCol && c !== titleCol);

  if (isEditing && canEdit) {
    return (
      <div className={styles.card}>
        <div className={styles.cardFields}>
          {columns.map((col) => (
            <div key={col.field} className={styles.cardField}>
              <span className={styles.cardFieldLabel}>{col.header}</span>
              {isCellReadOnly(opportunity, col, isAdmin) ? (
                <span className={styles.cardFieldValue}>{getDisplayValue(opportunity, col)}</span>
              ) : col.isDate ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={getEditValue(editValues, col) ? dayjs(getEditValue(editValues, col)) : null}
                    onChange={handleDateChange(col.field, !!col.isPrimarySort)}
                    slotProps={{
                      textField: { size: "small", variant: "standard", fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              ) : (
                <TextField
                  size="small"
                  variant="standard"
                  fullWidth
                  value={getEditValue(editValues, col) ?? ""}
                  onChange={handleFieldChange(col.field, !!col.isPrimarySort)}
                />
              )}
            </div>
          ))}
          {hasSubcategories && isAdmin && (
            <div className={styles.cardField}>
              <span className={styles.cardFieldLabel}>Subcategory</span>
              <Select
                size="small"
                variant="standard"
                fullWidth
                value={editValues.subcategory_id ?? ""}
                onChange={(e) => setEditValues((prev) => ({ ...prev, subcategory_id: e.target.value }))}
                required
              >
                {subcategories.map((sub) => (
                  <MuiMenuItem key={sub.id} value={sub.id}>{sub.name}</MuiMenuItem>
                ))}
              </Select>
            </div>
          )}
        </div>
        <div className={styles.cardActions}>
          <IconButton size="small" onClick={handleSave} color="primary" disabled={updateMutation.isPending || (isAdmin && hasSubcategories && !editValues.subcategory_id)}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} disabled={updateMutation.isPending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>
          {getDisplayValue(opportunity, titleCol) || "—"}
        </span>
        <span className={styles.cardDate}>
          {getDisplayValue(opportunity, dateCol)}
        </span>
      </div>
      <div className={styles.cardFields}>
        {detailCols.map((col) => (
          <div key={col.field} className={styles.cardField}>
            <span className={styles.cardFieldLabel}>{col.header}</span>
            <span className={styles.cardFieldValue}>
              {getDisplayValue(opportunity, col) || "—"}
            </span>
          </div>
        ))}
      </div>
      {canEdit && (
        <div className={styles.cardActions}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={handleDelete} color="error">
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
